/**
 * MediaSim User Context
 *
 * On mount (after authentication) this upserts the signed-in user into the
 * mediasim_users table and loads their DB-backed role. Admin status comes from
 * this table, NOT from Cognito group claims — the frontend drives user
 * management via API routes.
 */

import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { useRegisterUser } from '../../../api/media-sim/users';
import { useUser } from '../../../auth/hooks';
import type { MediaSimUserRecord } from '../../../types/media-sim';

interface MediaSimUserContextValue {
  /** The current user's mediasim_users row, or null until loaded. */
  record: MediaSimUserRecord | null;
  /** True once the initial register/load has settled. */
  loaded: boolean;
  isAdmin: boolean;
  /** Re-run the upsert/load (e.g. after a role change to self). */
  refresh: () => void;
}

const MediaSimUserContext = createContext<MediaSimUserContextValue | undefined>(undefined);

export function MediaSimUserProvider({ children }: { children: ReactNode }) {
  const authUser = useUser();
  const { registerUser } = useRegisterUser();
  const [record, setRecord] = useState<MediaSimUserRecord | null>(null);
  const [loaded, setLoaded] = useState(false);
  // Guard against duplicate upserts from React StrictMode double-invocation.
  const inFlight = useRef(false);

  const load = () => {
    if (!authUser?.sub || inFlight.current) return;
    inFlight.current = true;
    registerUser({ email: authUser.email, name: authUser.name })
      .then((r) => {
        if (r) setRecord(r);
      })
      .catch(() => {
        // A failed upsert leaves the user as non-admin; the app still works
        // for standard-user flows. Errors surface on the pages that need the role.
      })
      .finally(() => {
        setLoaded(true);
        inFlight.current = false;
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.sub]);

  const value: MediaSimUserContextValue = {
    record,
    loaded,
    isAdmin: record?.role === 'admin',
    refresh: load,
  };

  return (
    <MediaSimUserContext.Provider value={value}>{children}</MediaSimUserContext.Provider>
  );
}

export function useMediaSimUser(): MediaSimUserContextValue {
  const ctx = useContext(MediaSimUserContext);
  if (!ctx) {
    throw new Error('useMediaSimUser must be used within a MediaSimUserProvider');
  }
  return ctx;
}
